// ---------------------------------------------------------------------------
// SWEEP §4 / 0179 — THE RICHER WORKER PROFILE.
//
// Languages, skill tags and one emergency contact, on two surfaces:
//
//   WorkerProfilePanel   the console's worker detail screen.
//   MyProfile            the worker app, "About me".
//
// THE SCREEN ASKS NO PERMISSION QUESTION. The profile endpoint sends `may_*`
// flags decided against the bench row, and whether the contact may be SEEN at
// all — so "you cannot see this" and "none recorded" are different sentences
// rather than the same empty space. Every level and relationship comes from the
// payload with its label; no id is spelled here.
//
// No photo: storage and consent are separate questions.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState, type ReactNode } from "react";

import { requests, send } from "../api/auth";
import { console_, type WorkerProfile } from "../api/console";
import { privacy } from "../api/privacy";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { useT } from "../i18n/LocaleProvider";
import { resolveLocale, translateLabel, type Locale } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";

type Loaded<T> = { state: "loading" } | { state: "error"; message: string } | { state: "ready"; data: T };
type Reply = { status: number; body: WorkerProfile & { error?: { message?: string } } };

function useProfile(workerId: string) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [loaded, setLoaded] = useState<Loaded<WorkerProfile>>({ state: "loading" });
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!workerId) {
      setLoaded({ state: "error", message: t("There is no bench record on this account yet.") });
      return;
    }
    try {
      const { status, body } = await send<WorkerProfile & { error?: { message?: string } }>(
        console_.getWorkerProfile(apiBase, workerId),
      );
      if (status === 200 && body.languages) setLoaded({ state: "ready", data: body });
      else setLoaded({ state: "error", message: refusal(body.error) ?? t("We could not load this profile.") });
    } catch {
      setLoaded({ state: "error", message: t("We could not reach Verbunk.") });
    }
  }, [apiBase, workerId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Every write answers with the profile as it now is, so the screen shows what
  // was stored rather than what it sent.
  async function write(run: () => Promise<Reply>, done: string, failed: string): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setNotice(null);
    try {
      const { status, body } = await run();
      if (status !== 200 || !body.languages) {
        setNotice({ ok: false, text: refusal(body.error) ?? failed });
        return false;
      }
      setLoaded({ state: "ready", data: body });
      setNotice({ ok: true, text: done });
      return true;
    } catch {
      setNotice({ ok: false, text: t("We could not reach Verbunk. Check your connection and try again.") });
      return false;
    } finally {
      setBusy(false);
    }
  }

  return { loaded, notice, busy, write, load };
}

// ===========================================================================
// LANGUAGES — chosen from the list (R14, 0266), edited as a list, saved whole.
// ===========================================================================
function Languages({
  profile,
  busy,
  onSave,
  self,
}: {
  profile: WorkerProfile;
  busy: boolean;
  onSave: (languages: { language: string; level: string }[]) => Promise<boolean>;
  self: boolean;
}) {
  const { locale, t } = useT();
  const [draft, setDraft] = useState(profile.languages.map((l) => ({ language: l.language, level: l.level })));
  const [language, setLanguage] = useState("");
  // NO LEVEL IS PRESELECTED. The form must not decide how well somebody speaks.
  const [level, setLevel] = useState("");
  const levelOf = (id: string) => translateLabel(locale, profile.language_levels.find((l) => l.id === id)?.label ?? id);
  // Language names are listed, so each starts with a capital: the database words some of them
  // in lower case (afrikaans, hindi) and others not, which read as a mistake in one list.
  const cap = (name: string) => name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  const nameOf = (code: string) =>
    cap(translateLabel(
      locale,
      profile.languages_available.find((l) => l.id === code)?.label ??
        profile.languages.find((l) => l.language === code)?.language_label ??
        code,
    ));

  useEffect(() => {
    setDraft(profile.languages.map((l) => ({ language: l.language, level: l.level })));
  }, [profile.languages]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>{self ? t("Languages I speak") : t("Languages")}</h3>
      </div>
      {draft.length === 0 ? <p className="muted">{t("No languages recorded.")}</p> : null}
      <ul className="plain-list stack">
        {draft.map((l) => (
          <li key={l.language} className="row">
            <span>
              {nameOf(l.language)} — {levelOf(l.level)}
            </span>
            {profile.may_edit_languages ? (
              <button
                type="button"
                className="btn btn-quiet"
                disabled={busy}
                onClick={() => setDraft(draft.filter((d) => d !== l))}
              >
                {t("Remove {item}", { item: nameOf(l.language) })}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {profile.unmapped_languages.length > 0 ? (
        <p className="muted">
          {t("Written before languages were chosen from a list, and not on it: {items}. Choose them from the list; they go when the languages are next saved.", {
            items: profile.unmapped_languages.join(", "),
          })}
        </p>
      ) : null}
      {profile.may_edit_languages ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (busy) return;
            void onSave(draft);
          }}
        >
          <div className="row">
            <div className="field">
<label htmlFor="pf-1">{t("Language")}</label>
<select id="pf-1" value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="">{t("Choose…")}</option>
                {profile.languages_available.map((l) => (
                  <option key={l.id} value={l.id}>
                    {cap(translateLabel(locale, l.label))}
                  </option>
                ))}
              </select>
</div>
            <div className="field">
<label htmlFor="pf-2">{t("How well")}</label>
<select id="pf-2" value={level} onChange={(e) => setLevel(e.target.value)}>
                <option value="">{t("Choose…")}</option>
                {profile.language_levels.map((l) => (
                  <option key={l.id} value={l.id} title={l.description}>
                    {translateLabel(locale, l.label)}
                  </option>
                ))}
              </select>
</div>
            <button
              type="button"
              className="btn btn-quiet"
              disabled={busy || language === "" || level === ""}
              onClick={() => {
                setDraft([...draft.filter((d) => d.language !== language), { language, level }]);
                setLanguage("");
                setLevel("");
              }}
            >
              {t("Add to the list")}
            </button>
          </div>
          <div className="row">
            <button type="submit" className="btn" disabled={busy}>
              {t("Save languages")}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

// ===========================================================================
// SKILLS — the agency's own list (R14, 0266). A worker sees their own and
// cannot change them. Whoever may tag people may also add a tag to the list,
// or retire one; a retired tag stays on the people who carry it.
// ===========================================================================
function Skills({
  profile,
  busy,
  onSave,
  onListChanged,
  self,
}: {
  profile: WorkerProfile;
  busy: boolean;
  onSave: (skills: string[]) => Promise<boolean>;
  onListChanged: () => Promise<void>;
  self: boolean;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [draft, setDraft] = useState(profile.skills.map((s) => s.id));
  const [chosen, setChosen] = useState("");
  const [fresh, setFresh] = useState("");
  const [listBusy, setListBusy] = useState(false);
  const [listNotice, setListNotice] = useState<string | null>(null);
  const labelOf = (id: string) =>
    translateLabel(locale, profile.skill_tags.find((s) => s.id === id)?.label ?? profile.skills.find((s) => s.id === id)?.label ?? id);
  const offered = profile.skill_tags.filter((s) => !draft.includes(s.id));

  // Keyed on what is stored, not on the array: reloading the list after a tag
  // is added must not throw away skills chosen but not yet saved.
  const stored = profile.skills.map((s) => s.id).join(",");
  useEffect(() => {
    setDraft(stored === "" ? [] : stored.split(","));
  }, [stored]);

  async function listAct(run: () => Promise<{ status: number; body: { skill_tag?: { id: string }; error?: { message?: string } } }>) {
    setListBusy(true);
    setListNotice(null);
    try {
      const { status, body } = await run();
      if (status !== 200) {
        setListNotice(refusal(body.error) ?? t("The skill list could not be changed."));
        return null;
      }
      await onListChanged();
      return body.skill_tag ?? null;
    } catch {
      setListNotice(t("We could not reach Verbunk. Check your connection and try again."));
      return null;
    } finally {
      setListBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>{self ? t("Skills the agency has noted") : t("Skills")}</h3>
      </div>
      {draft.length === 0 ? <p className="muted">{t("No skills recorded.")}</p> : null}
      <ul className="plain-list row">
        {draft.map((id) => (
          <li key={id}>
            {profile.may_edit_skills ? (
              <button
                type="button"
                className="btn btn-quiet"
                disabled={busy}
                aria-label={t("Remove {item}", { item: labelOf(id) })}
                onClick={() => setDraft(draft.filter((d) => d !== id))}
              >
                {labelOf(id)} ×
              </button>
            ) : (
              <span>{labelOf(id)}</span>
            )}
          </li>
        ))}
      </ul>
      {/* Rule 2 of 06-compliance.md §8: a tag is a word about the work. Nothing about health or belief. */}
      {profile.may_edit_skills ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy) void onSave(draft);
          }}
        >
          <div className="row">
            <div className="field">
<label htmlFor="pf-3">{t("Skill")}</label>
<select id="pf-3" value={chosen} onChange={(e) => setChosen(e.target.value)}>
                <option value="">{t("Choose…")}</option>
                {offered.map((s) => (
                  <option key={s.id} value={s.id}>
                    {translateLabel(locale, s.label)}
                  </option>
                ))}
              </select>
</div>
            <button
              type="button"
              className="btn btn-quiet"
              disabled={busy || chosen === ""}
              onClick={() => {
                setDraft([...draft, chosen]);
                setChosen("");
              }}
            >
              {t("Add to the list")}
            </button>
            {chosen !== "" ? (
              <button
                type="button"
                className="btn btn-quiet"
                disabled={listBusy}
                onClick={() => void listAct(() => send(console_.retireSkillTag(apiBase, chosen))).then(() => setChosen(""))}
              >
                {t("Retire this skill from the agency's list")}
              </button>
            ) : null}
          </div>
          <div className="row">
            <div className="field">
<label htmlFor="pf-4">{t("A skill the list does not have yet")}</label>
<input id="pf-4" value={fresh} maxLength={40} onChange={(e) => setFresh(e.target.value)} />
</div>
            <button
              type="button"
              className="btn btn-quiet"
              disabled={listBusy || fresh.trim() === ""}
              onClick={() =>
                void listAct(() => send(console_.createSkillTag(apiBase, fresh.trim()))).then((tag) => {
                  if (!tag) return;
                  setDraft((d) => (d.includes(tag.id) ? d : [...d, tag.id]));
                  setFresh("");
                })
              }
            >
              {t("Add it to the agency's list")}
            </button>
          </div>
          {listNotice ? (
            <div className="form-message" role="alert">
              <StatusChip tone="attention" label={t("Not changed")} />
              <span>{listNotice}</span>
            </div>
          ) : null}
          <p className="muted">{t("A skill is about the work. Never record anything about health, beliefs or family here.")}</p>
          <div className="row">
            <button type="submit" className="btn" disabled={busy}>
              {t("Save skills")}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

// ===========================================================================
// THE EMERGENCY CONTACT
// ===========================================================================
function EmergencyContact({
  profile,
  busy,
  onSave,
  onRemove,
  self,
}: {
  profile: WorkerProfile;
  busy: boolean;
  onSave: (contact: { name: string; relationship: string; phone: string }) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
  self: boolean;
}) {
  const { locale, t } = useT();
  const current = profile.emergency_contact;
  const [name, setName] = useState(current?.name ?? "");
  const [relationship, setRelationship] = useState(current?.relationship ?? "");
  const [phone, setPhone] = useState(current?.phone ?? "");

  useEffect(() => {
    setName(current?.name ?? "");
    setRelationship(current?.relationship ?? "");
    setPhone(current?.phone ?? "");
  }, [current]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>{self ? t("Who to call if something happens to me") : t("Emergency contact")}</h3>
      </div>
      {!profile.emergency_contact_visible ? (
        <p className="muted">{t("Your role cannot see emergency contacts.")}</p>
      ) : (
        <>
          {current ? (
            <dl className="facts">
              <dt>{t("Name")}</dt>
              <dd>{current.name}</dd>
              <dt>{t("Who they are")}</dt>
              <dd>{translateLabel(locale, current.relationship_label)}</dd>
              <dt>{t("Phone")}</dt>
              <dd>
                <a href={`tel:${current.phone.replace(/[^0-9+]/g, "")}`}>{current.phone}</a>
              </dd>
            </dl>
          ) : (
            <p className="muted">{t("No emergency contact recorded.")}</p>
          )}
          {profile.may_edit_contact ? (
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                if (!busy) void onSave({ name, relationship, phone });
              }}
            >
              <div className="field">
<label htmlFor="pf-5">{t("Their name")}</label>
<input id="pf-5" value={name} maxLength={120} required onChange={(e) => setName(e.target.value)} />
</div>
              <div className="field">
<label htmlFor="pf-6">{self ? t("Who they are to you") : t("Who they are to {name}", { name: profile.worker.given_name })}</label>
<select id="pf-6" value={relationship} required onChange={(e) => setRelationship(e.target.value)}>
                  <option value="">{t("Choose…")}</option>
                  {profile.relationships.map((r) => (
                    <option key={r.id} value={r.id}>
                      {translateLabel(locale, r.label)}
                    </option>
                  ))}
                </select>
</div>
              <div className="field">
<label htmlFor="pf-7">{t("Their phone number")}</label>
<input id="pf-7" type="tel" value={phone} maxLength={24} required onChange={(e) => setPhone(e.target.value)} />
</div>
              <div className="row">
                <button type="submit" className="btn" disabled={busy}>
                  {t("Save emergency contact")}
                </button>
                {current ? (
                  <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void onRemove()}>
                    {t("Remove emergency contact")}
                  </button>
                ) : null}
              </div>
            </form>
          ) : null}
        </>
      )}
    </section>
  );
}

// ===========================================================================
// THE TWO SURFACES
// ===========================================================================
function ProfilePanels({ workerId, self, appLanguage }: { workerId: string; self: boolean; appLanguage?: ReactNode }) {
  const { apiBase } = useSession();
  const { locale, t } = useT();
  const { loaded, notice, busy, write, load } = useProfile(workerId);

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;
  if (loaded.state === "error") {
    return (
      <section className="panel">
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone="failed" label={t("Not loaded")} />
          <span>{loaded.message}</span>
        </div>
      </section>
    );
  }
  const profile = loaded.data;

  const languages = (
      <Languages
        profile={profile}
        busy={busy}
        self={self}
        onSave={(languages) =>
          write(
            () => send(console_.setWorkerLanguages(apiBase, workerId, languages)),
            t("The languages have been saved."),
            t("The languages could not be saved."),
          )
        }
      />
  );
  const contact = (
      <EmergencyContact
        profile={profile}
        busy={busy}
        self={self}
        onSave={(contact) =>
          write(
            () => send(console_.setEmergencyContact(apiBase, workerId, contact)),
            t("The emergency contact has been saved."),
            t("The emergency contact could not be saved."),
          )
        }
        onRemove={() =>
          write(
            () => send(console_.removeEmergencyContact(apiBase, workerId)),
            t("The emergency contact has been removed."),
            t("The emergency contact could not be removed."),
          )
        }
      />
  );
  const skills = (
      <Skills
        profile={profile}
        busy={busy}
        self={self}
        onListChanged={load}
        onSave={(skills) =>
          write(
            () => send(console_.setWorkerSkills(apiBase, workerId, skills)),
            t("The skills have been saved."),
            t("The skills could not be saved."),
          )
        }
      />
  );

  // On the worker's own phone the four parts are four tabs rather than one 2,200 px scroll.
  if (appLanguage !== undefined) {
    return (
      <div className="stack">
        {notice ? (
          <div className="form-message" role={notice.ok ? "status" : "alert"} aria-live="polite">
            <StatusChip tone={notice.ok ? "live" : "failed"} label={notice.ok ? t("Saved") : t("Not saved")} />
            <span>{notice.text}</span>
          </div>
        ) : null}
        <Tabs
          label={t("About me")}
          tabs={[
            { id: "prof-app", label: t("Language of the app"), content: appLanguage },
            { id: "prof-langs", label: t("Languages I speak"), content: languages },
            { id: "prof-contact", label: t("Emergency contact"), content: contact },
            { id: "prof-skills", label: t("Skills"), content: skills },
            { id: "prof-home", label: t("Home area"), content: <MyHomeArea workerId={workerId} /> },
            { id: "prof-rights", label: t("My data rights"), content: <MyDataRights workerId={workerId} /> },
          ]}
        />
      </div>
    );
  }

  return (
    <>
      {notice ? (
        <div className="form-message" role={notice.ok ? "status" : "alert"} aria-live="polite">
          <StatusChip tone={notice.ok ? "live" : "failed"} label={notice.ok ? t("Saved") : t("Not saved")} />
          <span>{notice.text}</span>
        </div>
      ) : null}
      {languages}
      {contact}
      {skills}
    </>
  );
}

/** On the console's worker detail screen. */
export function WorkerProfilePanel({ workerId }: { workerId: string }) {
  return <ProfilePanels workerId={workerId} self={false} />;
}

/**
 * SWEEP §4. The language the app speaks to this person.
 *
 * Stored on their account, not in this browser, so it follows them to a
 * borrowed phone. Empty is "automatic": the organisation's country decides.
 * Each language is named in itself, because somebody who cannot read the
 * current one must still find their own.
 */
function AppLanguage() {
  const { apiBase, me, reload } = useSession();
  const { locale, t } = useT();
  const stored = me?.authenticated ? (me.locale ?? "") : "";
  const [choice, setChoice] = useState(stored);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const NAMES: Record<Locale, string> = { en: "English", hu: "Magyar" };
  const automatic = NAMES[resolveLocale(null, me?.authenticated ? me.active_org?.country : null)];

  useEffect(() => setChoice(stored), [stored]);

  async function save() {
    setBusy(true);
    setNotice(null);
    try {
      const { status } = await send(requests.setLocale(apiBase, choice === "" ? null : choice));
      if (status !== 200) {
        setNotice({ ok: false, text: t("The language could not be saved.") });
        return;
      }
      // THE SESSION IS ASKED AGAIN, and the shell re-renders in the new language.
      await reload();
      setNotice({ ok: true, text: t("The language has been saved.") });
    } catch {
      setNotice({ ok: false, text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>{t("Language of the app")}</h3>
      </div>
      {notice ? (
        <div className="form-message" role={notice.ok ? "status" : "alert"} aria-live="polite">
          <StatusChip tone={notice.ok ? "live" : "failed"} label={notice.ok ? t("Saved") : t("Not saved")} />
          <span>{notice.text}</span>
        </div>
      ) : null}
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) void save();
        }}
      >
        <div className="field">
<label htmlFor="pf-8">{t("Language")}</label>
<select id="pf-8" value={choice} onChange={(e) => setChoice(e.target.value)}>
            <option value="">{t("Automatic ({language})", { language: automatic })}</option>
            {Object.entries(NAMES).map(([code, name]) => (
              <option key={code} value={code} lang={code}>
                {name}
              </option>
            ))}
          </select>
</div>
        <p className="muted">{t("Automatic follows the country of the organisation you work for.")}</p>
        <button type="submit" className="btn" disabled={busy || choice === stored}>
          {t("Save language")}
        </button>
      </form>
    </section>
  );
}

/** The worker app's "About me". */
export function MyProfile() {
  const { me } = useSession();
  const { locale, t } = useT();
  const workerId = (me?.authenticated ? me.worker_ids?.[0] : undefined) ?? "";
  return (
    <>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("About me")}</h2>
        </div>
        <p className="muted">
          {t(
            "The languages you speak help the agency offer you the right work. Your emergency contact is seen only by you and by the agency staff who look after people on the day or answer privacy requests — never by a client.",
          )}
        </p>
      </section>
      <ProfilePanels workerId={workerId} self appLanguage={<AppLanguage />} />
    </>
  );
}

/** Answer 64. A town or postcode, never an address; it only shows travel time. */
function MyHomeArea({ workerId }: { workerId: string }) {
  const { apiBase } = useSession();
  const { t } = useT();
  const [locality, setLocality] = useState("");
  const [postcode, setPostcode] = useState("");
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    void send<{ worker?: { home_locality: string | null; home_postcode: string | null } }>(
      console_.getWorker(apiBase, workerId),
    ).then((res) => {
      setLocality(res.body.worker?.home_locality ?? "");
      setPostcode(res.body.worker?.home_postcode ?? "");
    });
  }, [apiBase, workerId]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const res = await send(
      console_.setHomeArea(apiBase, workerId, { home_locality: locality || null, home_postcode: postcode || null }),
    );
    setSaid(res.status === 200
      ? { ok: true, text: t("Saved.") }
      : { ok: false, text: t("This could not be saved.") });
  }

  return (
    <section className="panel">
      <h3>{t("Home area")}</h3>
      <p className="muted">{t("A town or postcode, not an address. It only shows how long the journey to a venue is. It never scores or excludes you.")}</p>
      <form onSubmit={(e) => void save(e)}>
        <div className="field">
          <label htmlFor="home-town">{t("Town or city")}</label>
          <input id="home-town" value={locality} maxLength={120} onChange={(e) => setLocality(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="home-postcode">{t("Postcode")}</label>
          <input id="home-postcode" value={postcode} maxLength={12} onChange={(e) => setPostcode(e.target.value)} />
        </div>
        <button className="btn" type="submit">{t("Save")}</button>
      </form>
      {said ? <StatusChip tone={said.ok ? "live" : "failed"} label={said.text} /> : null}
    </section>
  );
}

type MyRequest = {
  id: string; kind_label: string; state_label: string; tone: string; is_terminal: boolean;
  received_at: string; due_at: string; closed_at: string | null;
};

/** Answer 63. The worker asks, the agency acts on the Privacy screen, and the deadline is the same one it counts. */
function MyDataRights({ workerId }: { workerId: string }) {
  const { apiBase } = useSession();
  const { locale, t } = useT();
  const [kinds, setKinds] = useState<{ id: string; label: string }[]>([]);
  const [kind, setKind] = useState("");
  const [requests, setRequests] = useState<MyRequest[]>([]);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);

  const load = () =>
    void send<{ requests?: MyRequest[] }>(console_.mySubjectRequests(apiBase)).then((res) => setRequests(res.body.requests ?? []));
  useEffect(() => {
    load();
    void send<{ request_kinds?: { id: string; label: string }[] }>(privacy.vocabulary(apiBase)).then((res) =>
      setKinds(res.body.request_kinds ?? []),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  async function ask(event: React.FormEvent) {
    event.preventDefault();
    const res = await send(
      privacy.openRequest(apiBase, { kind: "worker", id: workerId }, kind),
    );
    if (res.status === 201 || res.status === 200) {
      setSaid({ ok: true, text: t("Your agency has your request.") });
      setKind("");
      load();
    } else setSaid({ ok: false, text: t("The request could not be sent.") });
  }

  return (
    <section className="panel">
      <h3>{t("My data rights")}</h3>
      <p className="muted">{t("Ask your agency to act on your data. It has a deadline to answer, and you can follow it here.")}</p>
      <form onSubmit={(e) => void ask(e)}>
        <div className="field">
          <label htmlFor="right-kind">{t("What are you asking for?")}</label>
          <select id="right-kind" required value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">{t("Choose…")}</option>
            {kinds.map((k) => (
              <option key={k.id} value={k.id}>{translateLabel(locale, k.label)}</option>
            ))}
          </select>
        </div>
        <button className="btn" type="submit" disabled={!kind}>{t("Send the request")}</button>
      </form>
      {said ? <StatusChip tone={said.ok ? "live" : "failed"} label={said.text} /> : null}
      {requests.length > 0 ? (
        <ul>
          {requests.map((r) => (
            <li key={r.id}>
              {translateLabel(locale, r.kind_label)}: {translateLabel(locale, r.state_label)}
              {r.is_terminal ? null : (
                <span className="muted"> · {t("answer due by {date}", { date: new Date(r.due_at).toLocaleDateString(locale) })}</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

